import usePlatform from "@/utils/hooks/usePlatform";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import ModalHeader from "@/components/General/ModalHeader";
import { Keyboard, View, Modal, Pressable, ScrollView, Platform, StyleSheet as RNStyleSheet, useWindowDimensions } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import ReachToast from "./ReachToast";
import { useIsFocused } from "expo-router";
import { ConfirmationModal } from "@/utils/showAlert";
import Toast from "react-native-toast-message";
import useModalPortalStore, { ModalPortalContext } from "@/data/store/useModalPortalStore";
import useDropdownStore from '@/data/store/useDropdownStore';
import useGuideBlocker from '@/utils/hooks/useGuideBlocker';
import { useGuideSurface } from '@/components/Guides/GuideSurface';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";


/**
 * A component that displays a sheet modal.
 * @param {object} props - The properties for the SheetModal component.
 * @param {React.RefObject} props.sheetRef - A reference to the bottom sheet.
 * @param {JSX.Element} props.children - The content of the sheet modal.
 * @param {string} props.title - The title of the sheet modal.
 * @param {string} [props.subtitle] - The subtitle of the sheet modal.
 * @param {string} [props.cancelText="Close"] - The text for the cancel button.
 * @param {string} [props.confirmText=""] - The text for the confirm button.
 * @param {Function|null} [props.onCancel=null] - A callback function that is called when the sheet is canceled.
 * @param {Function|null} [props.onConfirm=null] - A callback function that is called when the sheet is confirmed.
 * @param {boolean} [props.loading=false] - Whether the sheet is currently loading.
 * @param {Function|undefined} [props.onShow=undefined] - A callback function that is called when the sheet is shown.
 * @param {string|undefined} [props.helpText=""] - The help text to display above the sheet.
 * @param {boolean} [props.stayOpenOnBlur=false] - When true, pushing another screen on top does NOT close the
 *   sheet. Instead the underlying RN Modal is hidden while unfocused (so it can't cover the pushed screen on
 *   Android / web) and restored when focus returns — parent open state is preserved. Default false keeps the
 *   legacy auto-close behavior.
 * @param {number} [props.width=500] - The width (in px) of the web side drawer. Falls back to screen width
 *   when the requested width exceeds the available screen width. Only applies on web; native is unchanged.
 * @param {boolean} [props.unmountOnClose=false] - When true, children unmount ~400ms after close
 *   (RN Modal keeps the subtree mounted forever otherwise). Children stay mounted through the
 *   animated close so the exit animation doesn't play on an empty panel.
 * @returns {JSX.Element} - The rendered sheet modal component.
 */
const SheetModal = ({isOpen, children, title, subtitle, centerTitle = false, cancelText = "Close", confirmText = "", onCancel = null, onClose = null, onConfirm = null, loading = false, onShow = undefined, helpText = "", stayOpenOnBlur = false, width = 500, scrollable = true, headerRight = null, allowGuideOverlay = false, guideCancelTarget = null, guideOverlay = null, onDismissed = null, guideSurfaceID = null, unmountOnClose = false}) => {
    const { isWeb, isNative } = usePlatform();
    const guideSurface = useGuideSurface(guideSurfaceID);
    const { width: screenWidth } = useWindowDimensions();
    const drawerWidth = Math.min(width, screenWidth);

    styles.useVariants({isWeb, isNative});
    const isFocused = useIsFocused();
    const modalNodeRef = useRef(null);
    // Latest focus state for the onDismiss guard — a ref because onDismiss can fire
    // after a blur-induced hide/unmount, when effect timing is unreliable.
    const isFocusedRef = useRef(isFocused);
    useEffect(() => {
        isFocusedRef.current = isFocused;
    }, [isFocused]);

    // Web drawer slide animation driven by shared values (not mount/unmount
    // exiting) so the exit animation plays reliably. isClosing is derived
    // synchronously during render (React "adjust state on prop change"
    // pattern) so the Modal stays mounted the instant isOpen flips false —
    // an effect-based approach leaves one frame where visible=false and the
    // Modal unmounts before the exit animation can play (flicker).
    const [isClosing, setIsClosing] = useState(false);
    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
    const dismissed = useRef(false);
    const dismissedCallback = useRef(onDismissed);
    dismissedCallback.current = () => { guideSurface.closed(); onDismissed?.(); };
    if (isOpen !== prevIsOpen) {
        setPrevIsOpen(isOpen);
        if (prevIsOpen && !isOpen) {
            setIsClosing(!dismissed.current);
        } else if (!prevIsOpen && isOpen) {
            setIsClosing(false);
        }
        dismissed.current = false;
    }

    // allowGuideOverlay: the sheet is hosting a guided tour right now, so it
    // must not mark itself as a guide blocker — the overlay renders above it.
    useGuideBlocker(isFocused && (isClosing || (isOpen && !allowGuideOverlay && !guideSurface.hosting)));

    // unmountOnClose: drop the child subtree shortly after close so a closed
    // sheet doesn't keep queries/subscribers/heavy views mounted forever.
    // Both platforms animate the close (web: 300ms slide+fade, native: RN
    // slide), so keep children mounted ~400ms — unmounting on isOpen=false
    // would empty the panel mid-animation. Cleanup clears on reopen/unmount.
    const [childrenMounted, setChildrenMounted] = useState(isOpen);
    useEffect(() => {
        if (!unmountOnClose) return;
        if (isOpen) {
            setChildrenMounted(true);
            return;
        }
        const timeout = setTimeout(() => setChildrenMounted(false), 400);
        return () => clearTimeout(timeout);
    }, [unmountOnClose, isOpen]);
    const renderChildren = !unmountOnClose || childrenMounted;

    const slideX = useSharedValue(isOpen ? 0 : screenWidth);
    const backdropOpacity = useSharedValue(isOpen ? 1 : 0);

    useEffect(() => {
        if (!isWeb) return;
        if (isOpen) {
            slideX.value = withTiming(0, {duration: 300});
            backdropOpacity.value = withTiming(1, {duration: 300});
        } else {
            slideX.value = withTiming(screenWidth, {duration: 300});
            backdropOpacity.value = withTiming(0, {duration: 300});
        }
    }, [isOpen, isWeb, screenWidth]);

    useEffect(() => {
        if (!isClosing || Platform.OS === 'ios') return;
        const t = setTimeout(() => { setIsClosing(false); dismissedCallback.current?.(); }, 300);
        return () => clearTimeout(t);
    }, [isClosing]);

    const panelAnimStyle = useAnimatedStyle(() => ({
        transform: [{translateX: slideX.value}],
    }));

    const backdropAnimStyle = useAnimatedStyle(() => ({
        opacity: backdropOpacity.value,
    }));

    // Register this modal's DOM node as a Radix Portal container for DropdownMenu
    // content on web. See useModalPortalStore for why this replaces the old
    // querySelectorAll('[aria-modal="true"]') lookup in DropdownMenu.
    const registerModalNode = useCallback((node) => {
        if (!isWeb) return;
        if (node) {
            modalNodeRef.current = node;
            useModalPortalStore.getState().register(node);
        } else {
            const prev = modalNodeRef.current;
            if (prev) useModalPortalStore.getState().unregister(prev);
            modalNodeRef.current = null;
        }
    }, [isWeb]);

    useEffect(() => () => {
        if (isWeb && modalNodeRef.current) useModalPortalStore.getState().unregister(modalNodeRef.current);
    }, [isWeb]);

    // Portal host provided to this sheet's subtree via ModalPortalContext —
    // where DropdownMenu/Zeego popovers portal on web. This must be the OUTER
    // container, not the registered inner panel: the panel keeps a Reanimated
    // translateX transform (panelAnimStyle) that captures position:fixed
    // descendants as its containing block, and its overflow:hidden clips them.
    const [portalHost, setPortalHost] = useState(null);
    const registerPortalHost = useCallback((node) => {
        if (isWeb) setPortalHost(node);
    }, [isWeb]);


    useEffect(() => {
        if (!isFocused && isOpen && !stayOpenOnBlur) {
          close();
        }
        // stayOpenOnBlur: keep state on blur — the RN Modal is just hidden
        // (visible={isOpen && isFocused}) so it can't float above the pushed screen,
        // and re-shows when focus returns.
      }, [isFocused, isOpen, stayOpenOnBlur]);

    useEffect(() => {
        if (!isWeb || !isOpen || !onConfirm) return;

        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                onConfirm();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [isWeb, isOpen, onConfirm]);

    const close = () => {
        Keyboard.dismiss();
        if(onCancel) onCancel();
        if(onClose) onClose();
    }

    return (
        <Modal
            onShow={() => {
                dismissed.current = false;
                if (isNative) {
                    // Dismiss any active keyboard before showing the modal
                    Keyboard.dismiss();
                }
                Toast.hide();
                guideSurface.shown();

                if (onShow) {
                    onShow();
                }
            }}
            onRequestClose={() => { if (!isWeb || !useDropdownStore.getState().open) close(); }}
            visible={(stayOpenOnBlur ? (isOpen && isFocused) : isOpen) || (isWeb && isClosing)}
            onDismiss={isNative ? () => {
                dismissed.current = true;
                setIsClosing(false);
                // Swallow dismissals caused by blur-hide/unmount (stayOpenOnBlur) —
                // only user-initiated dismissals (while focused) close the sheet.
                if (stayOpenOnBlur && !isFocusedRef.current) return;
                close();
                dismissedCallback.current?.();
            } : undefined}
            animationType={isNative ? "slide" : "fade"}
            transparent={isWeb}
            presentationStyle={isNative ? "pageSheet" : "overFullScreen"}
            statusBarTranslucent={isNative}
        >
            {isNative ? (
                <View style={styles.outer}>
                    <View style={styles.inner} aria-modal="true" role="dialog">
                        <ModalHeader
                            helpText={helpText}
                            cancelText={cancelText}
                            forSheet
                            onCancel={() => {
                                Keyboard.dismiss();
                                if(onCancel) onCancel();
                                if(onClose) onClose();
                            }}
                            goBackOnCancel={false}
                            loading={loading}
                            confirmText={confirmText}
                            onConfirm={onConfirm ? () => onConfirm() : undefined}
                            title={title}
                            subtitle={subtitle}
                            centerTitle={centerTitle}
                            headerRight={headerRight}
                            guideCancelTarget={guideCancelTarget}
                        />

                        {scrollable ? (
                            <ScrollView keyboardShouldPersistTaps="handled" style={styles.content} contentContainerStyle={{flexGrow: 1}} nestedScrollEnabled={true}>
                                {renderChildren ? children : null}
                            </ScrollView>
                        ) : <View style={styles.content}>{renderChildren ? children : null}</View>}

                        {/* Hosted guide overlay: drawn inside this sheet's own
                            view tree (not a nested Modal) so it can't land
                            underneath, and so its absolute positioning shares
                            the sheet's coordinate space. */}
                        {guideOverlay || guideSurface.overlay}
                    </View>
                </View>
            ) : (
                <Animated.View ref={isWeb ? registerPortalHost : undefined} style={[styles.outer, backdropAnimStyle]}>
                    <Pressable style={RNStyleSheet.absoluteFill} onPress={() => {
                        if (isClosing) return;
                        Keyboard.dismiss();
                        if(onCancel) onCancel();
                        if(onClose) onClose();
                    }} />
                    <Animated.View
                        ref={isWeb ? registerModalNode : undefined}
                        style={[styles.inner, panelAnimStyle, isWeb && {width: drawerWidth}]}
                        aria-modal="true"
                        role="dialog"
                    >
                        <ModalPortalContext.Provider value={portalHost}>
                            <ModalHeader
                                helpText={helpText}
                                cancelText={cancelText}
                                forSheet
                                onCancel={() => {
                                    Keyboard.dismiss();
                                    if(onCancel) onCancel();
                                    if(onClose) onClose();
                                }}
                                goBackOnCancel={false}
                                loading={loading}
                                confirmText={confirmText}
                                onConfirm={onConfirm ? () => onConfirm() : undefined}
                                title={title}
                                subtitle={subtitle}
                                centerTitle={centerTitle}
                                headerRight={headerRight}
                                guideCancelTarget={guideCancelTarget}
                            />

                            <View style={styles.content}>
                                {renderChildren ? children : null}
                            </View>
                            {guideOverlay || guideSurface.overlay}
                        </ModalPortalContext.Provider>
                    </Animated.View>
                </Animated.View>
            )}
            {isWeb && <ConfirmationModal />}
            {!isWeb && <ReachToast />}
        </Modal>
    );
}


const styles = StyleSheet.create(({colors}, {themeName, screen}) => {
    return {
        outer: {
            flex:1,
            variants: {
                isNative: {
                    true: {paddingHorizontal:15, backgroundColor:colors.background},
                    false: {flex:1, flexDirection:"row", justifyContent:"flex-end", alignItems:"stretch", backgroundColor:themeName == "dark" ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.3)", cursor: "pointer"}
                }
            }
        },
        inner:{
            variants: {
                isNative: {
                    true: {flex: 1},
                    false: {width:500, maxWidth:screen.width, height:"100%", overflow:"hidden", padding:20, backgroundColor:colors.background, boxShadow:"-4px 0px 16px 0px rgba(0,0,0,0.2)", cursor: "default"}
                }
            }
        },
        content: {
            variants: {
                isWeb: {
                    true: {flex:1, overflow:"auto", cursor: "default"},
                    false: {flex: 1}
                }
            }
        },
        overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            height: screen.height,
            width: screen.width,
            backgroundColor: "transparent",
            cursor: "pointer"
        }
    }
})

export default memo(SheetModal)